/**
 * IGNIERE §81 / §103 / §104: PDP ギャラリーを画像 alt の [色名] タグで絞り込む。
 *
 * 運用:
 *   画像 alt に `[色名]`（例：`[ブラック]`, `[レーシンググリーン]`）を書くと、
 *   その色が選択されている時だけ表示される。alt にタグが無い画像は常に表示（共通画像）。
 *   撤去された core §103/§104（product-media-gallery-content.liquid）の JS 版。
 *
 * 対象:
 *   - <slideshow-slide> 本体（メインギャラリー）
 *   - .slideshow-controls__thumbnail（サムネイル欄）— 同じ index で対応
 *
 * トリガー: 初回 apply + runtime.js の MutationObserver 再適用（色変更後 1〜1.5s の Section
 * Rendering API による DOM 差替に追随）。
 *
 * 冪等: apply 内で毎回全 slide を走査して現在色に合わせて display を再計算する（マーカー不要）。
 */
(function () {
  'use strict';
  const IG = window.IGNIERE_RUNTIME;
  if (!IG || !IG.register) return;

  const COLOR_OPTION_NAMES = ['color', 'colour', 'カラー', '色'];

  function getCurrentColorValue(root) {
    // .variant-option 内の checked radio から色オプションを特定
    const inputs = (root.querySelectorAll ? root : document).querySelectorAll(
      '.variant-option input[type="radio"]:checked'
    );
    for (const inp of inputs) {
      const optName = (inp.getAttribute('data-option-name') || '').toLowerCase();
      if (COLOR_OPTION_NAMES.indexOf(optName) !== -1) {
        return inp.value;
      }
    }
    return null;
  }

  // alt からタグ `[何か]` を抽出。無ければ null。
  function extractTag(alt) {
    if (!alt) return null;
    const m = alt.match(/\[([^\]]+)\]/);
    return m ? m[1] : null;
  }

  function shouldHide(alt, currentColor) {
    const tag = extractTag(alt);
    if (tag === null) return false; // タグ無し = 共通画像・常に表示
    return tag !== currentColor;
  }

  // フィルタ適用後、IGNIERE の scroll-hint（フェード）が古い状態のまま残るのを更新する。
  // 既存の scroll-hint 更新は MutationObserver(childList) なので display:none には反応しない。
  // scroll イベントを合成発火することで scroll ハンドラ経由で再計算させる。
  function refreshFadeHints() {
    const containers = document.querySelectorAll('.slideshow-controls__thumbnails-container');
    containers.forEach((c) => {
      try {
        c.dispatchEvent(new Event('scroll', { bubbles: true }));
      } catch (e) {}
    });
  }

  // §412（2026-11-03・代表「クイック追加で開くモーダルの左側が空」）：色は写真欄ごとに、同じ商品の欄の中の選択から決める。
  // 前はページの最初の色の選択（＝商品ページの色）を全部の写真欄に当てていて、商品ページから開いたクイック追加の小窓（#quick-add-modal-content の
  // 別の商品の写真欄）まで「今の色でない」として全部隠していた（本番でも・COMPOUND GILET の小窓は 6 枚とも隠れた）。
  function colorScopeOf(g) {
    return g.closest('#quick-add-modal-content') || g.closest('.shopify-section') || document;
  }

  IG.register('81-alt-filter', {
    apply(root) {
      if (!document.body) return;
      const galleries = document.querySelectorAll('media-gallery');
      if (!galleries.length) return;
      let anyChange = false;
      // §378-5（2026-10-27）絞る相手は商品の写真の欄（<media-gallery>）の中だけ（棚のカードの写真は触らない）
      galleries.forEach((g) => {
        const currentColor = getCurrentColorValue(colorScopeOf(g));
        if (!currentColor) return; // 色オプションが無い商品はスルー
        // メインスライドとサムネイル：自身の img.alt で判定・冪等（今の display と違う時だけ書き換え）
        g.querySelectorAll('slideshow-slide, .slideshow-controls__thumbnail').forEach((el) => {
          const img = el.querySelector('img');
          const alt = img ? img.getAttribute('alt') || '' : '';
          const desired = shouldHide(alt, currentColor) ? 'none' : '';
          if (el.style.display !== desired) {
            el.style.display = desired;
            anyChange = true;
          }
        });
      });

      // フェード再計算は状態が実際に変わった時だけ発火
      if (anyChange) refreshFadeHints();
    },
  });
})();

/**
 * §317-14（2026-10-13 代表の実機「サイズを選び直すたびに、ページが短く再読み込みの挙動をする。「ビクンっ」みたいな」）
 * 原因＝Horizon のコア assets/media-gallery.js が、種類を選ぶたびに店から取り寄せた写真の一式（<media-gallery>）で
 * 今の一式を丸ごと置き換える（replaceWith）。写真がまったく同じ（サイズだけ替えた）時も置き換えるので、
 * 写真の作り直し・見ていた写真から 1 枚目への戻り・上の色の絞り込みのかけ直しが起き、スマホでは「ビクン」と見える。
 * 直し（代表「その方法で直してください」）＝コアのファイルは書き換えず、置き換えの時に写真の並び（data-media-id の順）を比べ、
 * 同じなら今の一式を残す。違う時（色を替えて写真が変わる時など）は今まで通り置き換える。
 */
(function () {
  'use strict';
  if (!window.customElements || !customElements.whenDefined) return;
  function mediaOrder(g) {
    return Array.prototype.map.call(g.querySelectorAll('.product-media[data-media-id]'), function (el) {
      return el.getAttribute('data-media-id');
    }).join(',');
  }
  customElements.whenDefined('media-gallery').then(function () {
    var C = customElements.get('media-gallery');
    if (!C || C.prototype.__igKeepSameMedia) return;
    var original = Element.prototype.replaceWith;
    C.prototype.replaceWith = function (next) {
      try {
        if (arguments.length === 1 && next && next.tagName === 'MEDIA-GALLERY' && this.isConnected) {
          var now = mediaOrder(this);
          if (now && now === mediaOrder(next)) return; // 写真が同じ＝作り直さない
        }
      } catch (e) {}
      var result = original.apply(this, arguments);
      // 2026-10-25（代表「カラーを選択すると、そのカラーの画像のみが表示されなければならないのに、現状そうなっていません」）：
      // 色を替えて一式が作り直された直後に、色の絞り込みをかけ直す。runtime の見張りは、選ぶ部品の付け直し（§318）の
      // 直後 50ms は自分の変異を無視するので、その間に起きた作り直しを拾えず、他の色の写真が全部出ていた。
      try {
        var IG = window.IGNIERE_RUNTIME;
        if (IG && IG.modules && IG.modules['81-alt-filter']) IG.modules['81-alt-filter'].apply(document);
      } catch (e) {}
      return result;
    };
    C.prototype.__igKeepSameMedia = true;
  });
})();
